import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { ChartFavoriteRequest } from '@models/ChartFavorite/chartFavoriteRequest.model';
import { ChartFavoriteInfo } from '@models/ChartFavorite/chartfavoriteInfo.model';
import { DashboardInfo } from '@models/Dashboard/dashboardInfo.model';
import { DashboardRequest } from '@models/Dashboard/dashboardRequest.model';
import { ChartFavoriteWizardModel, createEmptyWizardState, DashboardWizardState, ImageFavoriteWizardModel, MapFavoriteWizardModel, NotificationWizardModel, WizardCreationType } from '@models/Dashboard/dashboardWizard.model';
import { DeviceInfo, SensorGroup } from '@models';
import { UserGroup } from '@models/User/usergroup.model';
import { ConfirmationService } from 'primeng/api';
import { ToastrService } from 'ngx-toastr';
import { ChartReadingsEchartModalComponent } from '../../review-readings/chart-readings-echart-modal/chart-readings-echart-modal.component';
import { SensorReadingsService } from '../../sensor-readings/sensor-readings.service';
import { DashboardEventsService } from '../dashboard.service';
import { DeviceService } from '@services/device.service';
import { SensorGroupService } from '@services/sensorgroup.service';
import { TriggerService } from '../../trigger-settings/trigger.service';
import { EventMapper } from '../../trigger-settings/trigger.mapping';
import { CommonShared } from 'app/shared/classes/common';
import { WizardPreviewInputs } from './chart-favorite-flow/chart-favorite-wizard-flow.component';
import { ImageFavoriteWizardFlowComponent } from './image-favorite-flow/image-favorite-wizard-flow.component';
import { MapFavoriteWizardFlowComponent, MapWizardPreviewInputs } from './map-favorite-flow/map-favorite-wizard-flow.component';
import { MapFavoriteInfo } from '@models/MapFavorite/mapFavoriteInfo.model';
import { MapFavoriteRequest } from '@models/MapFavorite/mapFavoriteRequest.model';
import { MapLabelInfo } from '@models/MapFavorite/mapLabelInfo.model';
import { MapDetails } from '@models/MapFavorite/mapDetails.model';
import { MapFavoriteService } from '@services/mapFavorite.service';
import { MapFavoriteComponent } from 'app/shared/components/map-favorite/map-favorite.component';
import { encodeDateRange } from 'app/shared/classes/dateRangeCodec';
import { ImageFavoriteInfo } from '@models/ImageFavorite/imageFavoriteInfo.model';
import { ImageFavoriteRequest } from '@models/ImageFavorite/imageFavoriteRequest.model';
import { ImageLabelInfo } from '@models/ImageFavorite/imageLabelInfo.model';
import { ImageSourceType } from '@models/ImageFavorite/imageFavorite.enums';
import { ImageFavoriteService } from '@services/imageFavorite.service';
import { ImageFavoriteGateway } from 'app/shared/components/image-favorite/image-favorite-edit-modal/image-favorite-edit-modal.component';
import { forkJoin, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

// Inline "Create" wizard that replaces the dashboard page's left list + right
// tabs while active. It owns the two-pane layout (stepper + steps on the left,
// a live preview on the right), the shared wizard state, and the footer. Step 1
// (the type chooser) is rendered here; steps 2-5 are delegated to the per-type
// flow component chosen from state.creationType (Chart Favorite, Notification
// or Image Favorite).
@Component({
  standalone: false,
  selector: 'dashboard-create-wizard',
  templateUrl: './dashboard-create-wizard.component.html',
  styleUrls: ['./dashboard-create-wizard.component.scss']
})
export class DashboardCreateWizardComponent implements OnInit {
  @Input() dashboards: DashboardInfo[] = [];
  @Input() associatedUserGroups: UserGroup[] = [];
  @Input() username = '';
  // When set, the wizard edits this existing chart favorite instead of creating one.
  @Input() editFavorite: ChartFavoriteInfo = null;
  // When set, the wizard edits this existing image favorite instead of creating one.
  @Input() editImageFavorite: ImageFavoriteInfo = null;
  // Scoutview gateways that can supply a dynamic image favorite background.
  @Input() gateways: ImageFavoriteGateway[] = [];
  // When set, the wizard edits this existing map favorite instead of creating one.
  @Input() editMapFavorite: MapFavoriteInfo = null;

  @Output() cancelled = new EventEmitter<void>();
  @Output() created = new EventEmitter<number>();
  // Emitted after an existing favorite is updated (edit mode).
  @Output() favoriteUpdated = new EventEmitter<void>();
  @Output() imageFavoriteUpdated = new EventEmitter<void>();
  @Output() mapFavoriteUpdated = new EventEmitter<void>();

  // Notification: 7 steps for the sensor-change trigger, 5 for timer/motion.
  // Map favorite: 4. Chart, Image (and before a type is chosen): 5.
  get totalSteps(): number {
    if (this.state.creationType === WizardCreationType.TriggerConditionsActions) {
      const tk = this.state.notification.triggerKind;
      return (tk === 'timer' || tk === 'motion') ? 5 : 7;
    }
    if (this.state.creationType === WizardCreationType.GeoMapSensorsReadings) {
      return 4;
    }
    return 5;
  }

  // The right-pane preview chart, used to capture the user's chart options
  // (type, colors, aggregates, ...) into the saved Chart Favorite's ChartDetails.
  @ViewChild('previewEchart') previewEchart: ChartReadingsEchartModalComponent;
  @ViewChild(ImageFavoriteWizardFlowComponent) imageFlow: ImageFavoriteWizardFlowComponent;
  @ViewChild(MapFavoriteWizardFlowComponent) mapFlow: MapFavoriteWizardFlowComponent;
  // The right-pane preview map: the live view and popups are read from it on Finish.
  @ViewChild('previewMap') previewMap: MapFavoriteComponent;

  // Stable stand-in favorite the right-pane image preview renders (draft mode).
  private imagePreviewFavoriteInfo = { id: 0, name: '', imageSourceType: ImageSourceType.Static } as ImageFavoriteInfo;

  // True while the Finish save is in flight (guards double-submit, disables Finish).
  saving = false;

  // Data the embedded event editor needs (loaded when the Notification path is active).
  editorDevices: DeviceInfo[] = [];
  editorSensorGroups: SensorGroup[] = [];
  editorGatewayNames: string[] = [];
  editorIrrZones: string[] = [];
  editorEvaluateTimes: string[] = [];
  private editorDataLoaded = false;

  // Stable empty inputs for the embedded editor (inline [] literals would trigger
  // ExpressionChanged errors in dev mode). Not needed for the deferred-save pass.
  notificationEditorEvents: any[] = [];
  notificationEditorSchedules: any[] = [];
  notificationEditorActionQueues: any[] = [];

  state: DashboardWizardState = createEmptyWizardState();

  // Whether the current (per-type) step passes validation. Driven by the flow
  // component for steps 2-5; step 1 uses isStep1Valid instead.
  stepValid = false;

  // Right-pane preview inputs; replaced wholesale when the flow emits a change.
  preview: WizardPreviewInputs = {
    sensors: [],
    sensorReadingTypes: [],
    sensorReadings: [],
    sensorGroups: [],
    dateRangeStart: null,
    includeCalValues: true,
    includeOrigValues: false,
    darkBackground: false,
    savedChartDetails: null
  };

  // Right-pane preview map inputs; replaced wholesale when the map flow emits a change.
  mapPreview: MapWizardPreviewInputs = { sensors: [], sensorReadings: [], savedMapDetails: null };

  private common = new CommonShared();

  constructor(
    private confirmationService: ConfirmationService,
    private toastr: ToastrService,
    private sensorReadingsService: SensorReadingsService,
    private dashboardEventsService: DashboardEventsService,
    private deviceService: DeviceService,
    private sensorGroupService: SensorGroupService,
    private triggerService: TriggerService,
    private imageFavoriteService: ImageFavoriteService,
    private mapFavoriteService: MapFavoriteService
  ) {}

  ngOnInit(): void {
    // Edit mode: skip the type chooser and open on the sensors step with all
    // steps reachable (the favorite's data is already valid).
    if (this.editFavorite != null) {
      this.state.creationType = WizardCreationType.ChartReadings;
      this.state.step = 2;
      this.state.furthestStepReached = this.totalSteps;
    } else if (this.editImageFavorite != null) {
      this.state.creationType = WizardCreationType.ImageMapReadings;
      this.state.step = 2;
      this.state.furthestStepReached = this.totalSteps;
    } else if (this.editMapFavorite != null) {
      this.state.creationType = WizardCreationType.GeoMapSensorsReadings;
      this.state.step = 2;
      this.state.furthestStepReached = this.totalSteps;
    }
  }

  get isEditMode(): boolean {
    return this.editFavorite != null || this.editImageFavorite != null || this.editMapFavorite != null;
  }

  get isChartFlow(): boolean {
    return this.state.creationType === WizardCreationType.ChartReadings;
  }

  get isNotificationFlow(): boolean {
    return this.state.creationType === WizardCreationType.TriggerConditionsActions;
  }

  get isImageFlow(): boolean {
    return this.state.creationType === WizardCreationType.ImageMapReadings;
  }

  get isMapFlow(): boolean {
    return this.state.creationType === WizardCreationType.GeoMapSensorsReadings;
  }

  get imagePreviewFavorite(): ImageFavoriteInfo {
    const m = this.state.imageFavorite;
    this.imagePreviewFavoriteInfo.name = (m.favoriteName ?? '').trim() !== '' ? m.favoriteName.trim() : 'Image Favorite';
    this.imagePreviewFavoriteInfo.imageSourceType = m.imageSourceType;
    return this.imagePreviewFavoriteInfo;
  }

  onImageLabelsChange(labels: ImageLabelInfo[]): void {
    this.state.imageFavorite.labels = labels;
    this.imageFlow?.onLabelsChanged();
  }

  onMapLabelsChange(labels: MapLabelInfo[]): void {
    this.state.mapFavorite.labels = labels;
    this.mapFlow?.onLabelsChanged();
  }

  // The preview map reports every view / popup change; keep the latest in wizard state.
  onMapDetailsChanged(details: MapDetails): void {
    this.state.mapFavorite.mapDetails = details;
  }

  onMapPreviewChanged(p: MapWizardPreviewInputs): void {
    Promise.resolve().then(() => (this.mapPreview = p));
  }

  get isStep1Valid(): boolean {
    return this.state.creationType != null;
  }

  get canGoNext(): boolean {
    return this.state.step === 1 ? this.isStep1Valid : this.stepValid;
  }

  get isLastStep(): boolean {
    return this.state.step === this.totalSteps;
  }

  onTypeSelected(type: WizardCreationType): void {
    this.state.creationType = type;
    if (type === WizardCreationType.TriggerConditionsActions) {
      this.state.notification.event.userName = this.username;
      this.loadNotificationEditorData();
    }
  }

  // Load the data the embedded event editor needs (once). Devices + sensor groups
  // come from services; evaluate-times and irrigation zones are generated locally
  // (mirrors trigger-settings' loaders).
  private loadNotificationEditorData(): void {
    if (this.editorDataLoaded) {
      return;
    }
    this.editorDataLoaded = true;

    for (let h = 0; h < 24; h++) {
      this.editorEvaluateTimes.push(this.pad2(h) + ':15');
      this.editorEvaluateTimes.push(this.pad2(h) + ':45');
    }
    for (let i = 0; i < 30; i++) {
      this.editorIrrZones.push(i.toString());
    }

    this.deviceService.getDevices().subscribe(
      results => {
        if (results != null) {
          this.editorDevices = results;
          this.editorGatewayNames = [];
          this.editorDevices.forEach(d => {
            const owned = d.userName === this.username || this.associatedUserGroups.findIndex(u => u.id === d.userGroupId) > -1;
            if (owned && d.serialNumber !== '') {
              this.editorGatewayNames.push(d.name);
            }
          });
        }
      },
      () => this.toastr.error('Error occurred while retrieving gateway devices')
    );

    this.sensorGroupService.getSensorGroupWithSensorsAndReadingTypes().subscribe(
      results => { if (results != null) { this.editorSensorGroups = results; } },
      () => this.toastr.error('Error occurred while retrieving sensor groups')
    );
  }

  private pad2(n: number): string {
    return (n < 10 ? '0' : '') + n;
  }

  // Deferred so a child emitting during its own init/ngOnChanges (inside the
  // container's change-detection pass) doesn't trigger ExpressionChanged errors.
  onStepValidChange(valid: boolean): void {
    Promise.resolve().then(() => (this.stepValid = valid));
  }

  onPreviewChanged(p: WizardPreviewInputs): void {
    Promise.resolve().then(() => (this.preview = {
      sensors: p.sensors,
      sensorReadingTypes: p.sensorReadingTypes,
      sensorReadings: p.sensorReadings,
      sensorGroups: p.sensorGroups,
      dateRangeStart: p.dateRangeStart,
      includeCalValues: p.includeCalValues,
      includeOrigValues: p.includeOrigValues,
      darkBackground: p.darkBackground,
      savedChartDetails: p.savedChartDetails
    }));
  }

  // Navigate when the user clicks a stepper circle: jump to any already-visited
  // step, or advance one step when they click the next circle and the current
  // step has enough data.
  goToStep(step: number): void {
    if (step >= 1 && step <= this.state.furthestStepReached) {
      this.state.step = step;
    } else if (step === this.state.step + 1 && this.canGoNext && step <= this.totalSteps) {
      this.next();
    }
  }

  next(): void {
    if (!this.canGoNext || this.state.step >= this.totalSteps) {
      return;
    }
    this.state.step++;
    this.state.furthestStepReached = Math.max(this.state.furthestStepReached, this.state.step);
  }

  // Finish: create the Chart Favorite, then create (or update) the dashboard it
  // attaches to. createChartFavorite returns only 'success' (no id), so the new
  // favorite's id is resolved by reloading favorites and matching on its (unique)
  // name — the same approach the Analyze Sensor Readings page uses.
  finish(): void {
    if (!this.canGoNext || this.saving) {
      return;
    }

    if (this.isNotificationFlow) {
      this.finishNotification();
      return;
    }

    if (this.isImageFlow) {
      this.finishImageFavorite();
      return;
    }

    if (this.isMapFlow) {
      this.finishMapFavorite();
      return;
    }

    this.saving = true;
    const cf = this.state.chartFavorite;

    // Edit mode: update the existing favorite in place; no dashboard attach step.
    if (this.isEditMode) {
      const updateRequest = this.buildChartFavoriteRequest(cf, this.editFavorite.id);
      this.sensorReadingsService.updateChartFavorite(updateRequest, this.username).subscribe(
        () => {
          this.saving = false;
          this.toastr.success('Chart favorite updated successfully.');
          this.favoriteUpdated.emit();
        },
        () => {
          this.saving = false;
          this.toastr.error('Error occurred while updating the chart favorite.');
        }
      );
      return;
    }

    const request = this.buildChartFavoriteRequest(cf);

    this.sensorReadingsService.createChartFavorite(request).subscribe(
      result => {
        if (result !== 'success') {
          this.saving = false;
          this.toastr.warning('Could not save the chart favorite.');
          return;
        }
        this.sensorReadingsService.getChartFavorites().subscribe(
          favorites => {
            const saved = (favorites || []).find((c: any) => c.name === request.name);
            if (saved == undefined) {
              this.saving = false;
              this.toastr.error('Chart favorite saved, but it could not be located to attach to a dashboard.');
              return;
            }
            this.toastr.success('Chart favorite created successfully.');
            this.saveDashboard(saved.id, cf);
          },
          () => {
            this.saving = false;
            this.toastr.error('Chart favorite saved, but loading favorites failed.');
          }
        );
      },
      () => {
        this.saving = false;
        this.toastr.error('Error occurred while saving the chart favorite.');
      }
    );
  }

  // Build the Chart Favorite payload from the wizard state + the preview chart's
  // current options (mirrors review-readings' setChartFavoriteRequest/buildChartDetails).
  private buildChartFavoriteRequest(cf: ChartFavoriteWizardModel, id: number = -1): ChartFavoriteRequest {
    const groupIds = cf.selectedSensorGroups.map(g => g.id);
    return {
      id: id,
      name: cf.favoriteName.trim(),
      sensorGroupID: groupIds.length > 0 ? groupIds[0] : null,
      sensorGroupIDs: groupIds,
      sensorIDs: cf.selectedSensors.map(s => s.id),
      sensorReadingTypeIDs: cf.selectedReadingTypes.map(t => t.id),
      dateRange: this.encodeDateRange(cf),
      chartDetails: this.buildChartDetailsJson(cf),
      isManualScaling: false,
      scaleYMin: undefined,
      scaleYMax: undefined,
      outlierYMin: undefined,
      outlierYMax: undefined,
      userName: this.username,
      userGroupId: (cf.favoriteUserGroup != null && cf.favoriteUserGroup.id > -1) ? cf.favoriteUserGroup.id : null
    };
  }

  // A named preset label when one is selected, otherwise a "start,end" ISO string.
  private encodeDateRange(cf: ChartFavoriteWizardModel): string {
    if (cf.dateRangeName && cf.dateRangeName !== '' && cf.dateRangeName !== 'Custom Range') {
      return cf.dateRangeName;
    }
    return cf.dateRange.startDate.format('YYYY-MM-DDTHH:mm:ss') + ',' + cf.dateRange.endDate.format('YYYY-MM-DDTHH:mm:ss');
  }

  // The eChart's own options (chart type, colors, aggregates, ...) overlaid with
  // the wizard's host-level options (timezone, reading source).
  private buildChartDetailsJson(cf: ChartFavoriteWizardModel): string {
    const details: any = this.previewEchart != undefined ? this.previewEchart.getChartDetails() : {};
    details.timeZone = cf.selectedTimeZone;
    details.timeZoneOffset = cf.selectedTimeZoneOffset;
    details.showOriginalReadings = cf.showOriginalReadings;
    details.showCalibratedReadings = cf.showCalibratedReadings;
    details.darkBackground = false;
    return JSON.stringify(details);
  }

  // Attach the new favorite to a new or existing dashboard, then signal the
  // parent (which exits the wizard and selects the resulting dashboard).
  private saveDashboard(favoriteId: number, cf: ChartFavoriteWizardModel): void {
    if (cf.dashboardTargetMode === 'existing') {
      const d = cf.targetDashboard;
      const request: DashboardRequest = {
        id: d.id,
        name: d.name,
        eventIDs: d.eventIds || [],
        chartFavoriteIds: Array.from(new Set([...(d.chartFavoriteIds || []), favoriteId])),
        imageFavoriteIds: d.imageFavoriteIds || [],
        mapFavoriteIds: d.mapFavoriteIds || [],
        userName: this.username,
        userGroup: d.userGroup,
        userGroupID: d.userGroupId,
        includeChartsTab: 1,                               // ensure the chart is visible
        includeEventsTab: d.includeEventsTab,
        includeIsalTab: d.includeIsalTab,
        includeImageFavoritesTab: d.includeImageFavoritesTab,
        includeMapFavoritesTab: d.includeMapFavoritesTab,
        computeActionSumCountsForEvents: d.computeActionSumCountsForEvents
      };
      this.dashboardEventsService.updateDashboard(request, this.username).subscribe(
        () => {
          this.saving = false;
          this.toastr.success('Dashboard updated successfully.');
          this.created.emit(d.id);
        },
        () => {
          this.saving = false;
          this.toastr.error('Error occurred while updating the dashboard.');
        }
      );
    } else {
      const groupId = (cf.favoriteUserGroup != null && cf.favoriteUserGroup.id > -1) ? cf.favoriteUserGroup.id : null;
      const request: DashboardRequest = {
        id: undefined,
        name: cf.newDashboardName.trim(),
        eventIDs: [],
        chartFavoriteIds: [favoriteId],
        imageFavoriteIds: [],
        mapFavoriteIds: [],
        userName: this.username,
        userGroup: cf.favoriteUserGroup != null ? cf.favoriteUserGroup.name : '',
        userGroupID: groupId,
        includeChartsTab: 1,
        includeEventsTab: 0,
        includeIsalTab: 0,
        includeImageFavoritesTab: 0,
        includeMapFavoritesTab: 0,
        computeActionSumCountsForEvents: 0
      };
      this.dashboardEventsService.createDashboard(request).subscribe(
        result => {
          this.saving = false;
          this.toastr.success('Dashboard created successfully.');
          this.created.emit(typeof result === 'number' ? result : null);
        },
        () => {
          this.saving = false;
          this.toastr.error('Error occurred while creating the dashboard.');
        }
      );
    }
  }

  // ---------- Notification path save ----------

  // Finish for the Notification path: resolve the sensor selection to a real
  // group (find owned exact-match, else create), create the Event, reload events
  // to recover its id (create returns only 'success'), then attach it to a new or
  // existing dashboard with the events tab enabled.
  private finishNotification(): void {
    this.saving = true;
    const n = this.state.notification;
    n.event.name = (n.event.name || '').trim();

    // Timer and motion events have no sensor conditions — create directly.
    // Sensor-change events first resolve/create the sensor group they watch.
    if (n.triggerKind === 'timer' || n.triggerKind === 'motion') {
      this.createEventAndAttach(n);
      return;
    }

    this.resolveSensorGroup(n, group => {
      if (group == null) { this.saving = false; return; }   // error already surfaced
      n.event.sensorConditions[0].sensorGroup = group;
      n.event.sensorConditions[0].sensorGroupID = group.id;
      this.createEventAndAttach(n);
    });
  }

  // Create the Event, reload events to recover its id (create returns only
  // 'success'), then attach it to the chosen dashboard.
  private createEventAndAttach(n: NotificationWizardModel): void {
    const eventInfo = new EventMapper().mapEvent2EventInfo(n.event);
    this.triggerService.createEvent(eventInfo).subscribe(
      result => {
        if (result == null) { this.saving = false; this.toastr.warning('Could not save the notification.'); return; }
        this.triggerService.getEvents('Loading events ...', true).subscribe(
          (events: any[]) => {
            const saved = (events || []).find((e: any) => e.name === n.event.name);
            if (saved == undefined) {
              this.saving = false;
              this.toastr.error('Notification saved, but it could not be located to attach to a dashboard.');
              return;
            }
            this.toastr.success('Notification created successfully.');
            this.attachEventToDashboard(saved.id, n);
          },
          () => { this.saving = false; this.toastr.error('Notification saved, but loading events failed.'); }
        );
      },
      () => { this.saving = false; this.toastr.error('Error occurred while saving the notification.'); }
    );
  }

  // Resolve the user's sensor selection to a persisted sensor group, calling back
  // with it (or null on error). A whole existing group chosen in the tree is used
  // directly; otherwise find a user-owned group whose sensors exactly match, else
  // create a new group (create returns 'success', so reload + match by name).
  private resolveSensorGroup(n: NotificationWizardModel, done: (group: SensorGroup | null) => void): void {
    const condition = n.event.sensorConditions[0];
    if (condition.sensorGroup != null && condition.sensorGroup.id > 0) {
      done(condition.sensorGroup);
      return;
    }
    const selectedIds = new Set(n.selectedSensors.map(s => s.id));
    if (selectedIds.size === 0) {
      this.saving = false;
      this.toastr.error('Select at least one sensor to watch.');
      done(null);
      return;
    }
    this.sensorGroupService.getSensorGroupsWithSensors(true).subscribe(
      (groups: SensorGroup[]) => {
        const match = (groups || []).find(g => this.ownsGroup(g) && this.sameSensorSet(g, selectedIds));
        if (match != undefined) { done(match); return; }
        this.createSensorGroupForSelection(n, groups || [], done);
      },
      () => { this.saving = false; this.toastr.error('Error occurred while loading sensor groups.'); done(null); }
    );
  }

  private ownsGroup(g: SensorGroup): boolean {
    return g.userName === this.username || this.associatedUserGroups.findIndex(u => u.id === g.userGroupId) > -1;
  }

  private sameSensorSet(g: SensorGroup, selectedIds: Set<number>): boolean {
    const memberIds = (g.sensors ?? []).map(s => s.id);
    if (memberIds.length !== selectedIds.size) { return false; }
    return memberIds.every(id => selectedIds.has(id));
  }

  // Create a new sensor group from the selected sensors, then reload + match by
  // name to recover its id.
  private createSensorGroupForSelection(n: NotificationWizardModel, existing: SensorGroup[], done: (group: SensorGroup | null) => void): void {
    const name = this.uniqueGroupName(existing, n);
    const now = new Date();
    const group = {
      id: 0,
      name,
      description: 'Created by the dashboard wizard',
      createdDate: now,
      modifiedDate: now,
      isDisabled: false,
      crop: '-',
      plantAge: 0,
      userGroupId: null,
      userGroup: '',
      userName: this.username,
      events: [],
      sensors: n.selectedSensors.slice(),
      isNew: true
    } as SensorGroup;
    this.common.updateUserInfoForSensorGroup(group, n.accessUserGroup, this.username);
    const info = this.common.mapSensorGroup2SensorGroupInfo(group);
    info.endDate = undefined;

    this.sensorGroupService.createSensorGroup(info).subscribe(
      result => {
        if (result == null) { this.saving = false; this.toastr.warning('Could not create the sensor group.'); done(null); return; }
        this.sensorGroupService.getSensorGroupsWithSensors(true).subscribe(
          (groups: SensorGroup[]) => {
            const created = (groups || []).find(g => g.name === name);
            if (created == undefined) { this.saving = false; this.toastr.error('Sensor group created, but it could not be located.'); done(null); return; }
            done(created);
          },
          () => { this.saving = false; this.toastr.error('Error reloading sensor groups.'); done(null); }
        );
      },
      () => { this.saving = false; this.toastr.error('Error occurred while creating the sensor group.'); done(null); }
    );
  }

  private uniqueGroupName(existing: SensorGroup[], n: NotificationWizardModel): string {
    const base = (n.event.name && n.event.name.trim()) ? (n.event.name.trim() + ' sensors') : 'Wizard group';
    const taken = new Set((existing || []).map(g => (g.name || '').toLowerCase()));
    let name = base;
    let i = 2;
    while (taken.has(name.toLowerCase())) { name = base + ' ' + i; i++; }
    return name;
  }

  // Attach the new event to a new or existing dashboard (events tab enabled).
  private attachEventToDashboard(eventId: number, n: NotificationWizardModel): void {
    if (n.dashboardTargetMode === 'existing') {
      const d = n.targetDashboard;
      const request: DashboardRequest = {
        id: d.id,
        name: d.name,
        eventIDs: Array.from(new Set([...(d.eventIds || []), eventId])),
        chartFavoriteIds: d.chartFavoriteIds || [],
        imageFavoriteIds: d.imageFavoriteIds || [],
        mapFavoriteIds: d.mapFavoriteIds || [],
        userName: this.username,
        userGroup: d.userGroup,
        userGroupID: d.userGroupId,
        includeChartsTab: d.includeChartsTab,
        includeEventsTab: 1,
        includeIsalTab: d.includeIsalTab,
        includeImageFavoritesTab: d.includeImageFavoritesTab,
        includeMapFavoritesTab: d.includeMapFavoritesTab,
        computeActionSumCountsForEvents: d.computeActionSumCountsForEvents
      };
      this.dashboardEventsService.updateDashboard(request, this.username).subscribe(
        () => { this.saving = false; this.toastr.success('Dashboard updated successfully.'); this.created.emit(d.id); },
        () => { this.saving = false; this.toastr.error('Error occurred while updating the dashboard.'); }
      );
    } else {
      const groupId = (n.accessUserGroup != null && n.accessUserGroup.id > -1) ? n.accessUserGroup.id : null;
      const request: DashboardRequest = {
        id: undefined,
        name: n.newDashboardName.trim(),
        eventIDs: [eventId],
        chartFavoriteIds: [],
        imageFavoriteIds: [],
        mapFavoriteIds: [],
        userName: this.username,
        userGroup: n.accessUserGroup != null ? n.accessUserGroup.name : '',
        userGroupID: groupId,
        includeChartsTab: 0,
        includeEventsTab: 1,
        includeIsalTab: 0,
        includeImageFavoritesTab: 0,
        includeMapFavoritesTab: 0,
        computeActionSumCountsForEvents: 0
      };
      this.dashboardEventsService.createDashboard(request).subscribe(
        result => { this.saving = false; this.toastr.success('Dashboard created successfully.'); this.created.emit(typeof result === 'number' ? result : null); },
        () => { this.saving = false; this.toastr.error('Error occurred while creating the dashboard.'); }
      );
    }
  }

  // ---------- Image Favorite path save ----------

  // Finish for the Image Favorite path: create (or update) the favorite, upload a
  // newly chosen static image, sync the draft labels (create the temporary
  // negative-id ones, update the rest, delete those removed while editing), then
  // attach a new favorite to a new or existing dashboard.
  private finishImageFavorite(): void {
    this.saving = true;
    const m = this.state.imageFavorite;
    const edit = this.editImageFavorite;
    const isDynamic = m.imageSourceType === ImageSourceType.Dynamic;
    const tag = (m.imageTag ?? '').trim();

    const request: ImageFavoriteRequest = {
      id: edit != null ? edit.id : 0,
      name: m.favoriteName.trim(),
      description: edit != null ? edit.description : null,
      imageSourceType: m.imageSourceType,
      gatewaySourceId: isDynamic ? m.gatewaySourceId : null,
      imageTag: isDynamic && tag !== '' ? tag : null,
      details: edit != null ? edit.details : null,
      userName: edit != null ? edit.userName : this.username,
      userGroupId: (m.favoriteUserGroup != null && m.favoriteUserGroup.id > -1) ? m.favoriteUserGroup.id : null
    };

    const save$: Observable<number> = edit != null
      ? this.imageFavoriteService.updateImageFavorite(request, this.username).pipe(map(() => edit.id))
      : this.imageFavoriteService.createImageFavorite(request).pipe(map((newId: any) => Number(newId)));

    save$.pipe(
      switchMap((id: number) => {
        if (isNaN(id) || id <= 0) {
          throw new Error('Invalid image favorite id');
        }
        return (!isDynamic && m.file != null)
          ? this.imageFavoriteService.uploadImage(id, m.file, this.username).pipe(map(() => id))
          : of(id);
      }),
      switchMap((id: number) => this.syncImageLabels(id, m).pipe(map(() => id)))
    ).subscribe(
      (id: number) => {
        if (edit != null) {
          this.saving = false;
          this.toastr.success('Image favorite updated successfully.');
          this.imageFavoriteUpdated.emit();
          return;
        }
        this.toastr.success('Image favorite created successfully.');
        this.attachImageFavoriteToDashboard(id, m);
      },
      () => {
        this.saving = false;
        this.toastr.error('Error occurred while saving the image favorite.');
      }
    );
  }

  private syncImageLabels(imageFavoriteId: number, m: ImageFavoriteWizardModel): Observable<any> {
    const keptIds = new Set(m.labels.map(l => l.id));
    const calls: Observable<any>[] = [
      ...m.labels.filter(l => l.id <= 0)
                 .map(l => this.imageFavoriteService.createImageLabel({ ...l, id: 0, imageFavoriteID: imageFavoriteId })),
      ...m.labels.filter(l => l.id > 0)
                 .map(l => this.imageFavoriteService.updateImageLabel({ ...l, imageFavoriteID: imageFavoriteId }, this.username)),
      ...m.originalLabelIds.filter(id => !keptIds.has(id))
                           .map(id => this.imageFavoriteService.deleteImageLabel(id, this.username))
    ];
    return calls.length > 0 ? forkJoin(calls) : of([]);
  }

  // Attach the new image favorite to a new or existing dashboard (image tab enabled).
  private attachImageFavoriteToDashboard(imageFavoriteId: number, m: ImageFavoriteWizardModel): void {
    if (m.dashboardTargetMode === 'existing') {
      const d = m.targetDashboard;
      const request: DashboardRequest = {
        id: d.id,
        name: d.name,
        eventIDs: d.eventIds || [],
        chartFavoriteIds: d.chartFavoriteIds || [],
        imageFavoriteIds: Array.from(new Set([...(d.imageFavoriteIds || []), imageFavoriteId])),
        mapFavoriteIds: d.mapFavoriteIds || [],
        userName: this.username,
        userGroup: d.userGroup,
        userGroupID: d.userGroupId,
        includeChartsTab: d.includeChartsTab,
        includeEventsTab: d.includeEventsTab,
        includeIsalTab: d.includeIsalTab,
        includeImageFavoritesTab: 1,                       // ensure the image favorite is visible
        includeMapFavoritesTab: d.includeMapFavoritesTab,
        computeActionSumCountsForEvents: d.computeActionSumCountsForEvents
      };
      this.dashboardEventsService.updateDashboard(request, this.username).subscribe(
        () => { this.saving = false; this.toastr.success('Dashboard updated successfully.'); this.created.emit(d.id); },
        () => { this.saving = false; this.toastr.error('Error occurred while updating the dashboard.'); }
      );
    } else {
      const groupId = (m.favoriteUserGroup != null && m.favoriteUserGroup.id > -1) ? m.favoriteUserGroup.id : null;
      const request: DashboardRequest = {
        id: undefined,
        name: m.newDashboardName.trim(),
        eventIDs: [],
        chartFavoriteIds: [],
        imageFavoriteIds: [imageFavoriteId],
        mapFavoriteIds: [],
        userName: this.username,
        userGroup: m.favoriteUserGroup != null ? m.favoriteUserGroup.name : '',
        userGroupID: groupId,
        includeChartsTab: 0,
        includeEventsTab: 0,
        includeIsalTab: 0,
        includeImageFavoritesTab: 1,
        includeMapFavoritesTab: 0,
        computeActionSumCountsForEvents: 0
      };
      this.dashboardEventsService.createDashboard(request).subscribe(
        result => { this.saving = false; this.toastr.success('Dashboard created successfully.'); this.created.emit(typeof result === 'number' ? result : null); },
        () => { this.saving = false; this.toastr.error('Error occurred while creating the dashboard.'); }
      );
    }
  }

  // ---------- Map Favorite path save ----------

  // Finish for the Map Favorite path: create (or update) the favorite with the preview
  // map's current view and open popups, sync the draft labels (create the temporary
  // negative-id ones, update the rest, delete those removed while editing), then attach
  // a new favorite to a new or existing dashboard.
  private finishMapFavorite(): void {
    const m = this.state.mapFavorite;
    const edit = this.editMapFavorite;

    // The live state of the preview map. It is not shown once the user has left step 3
    // for the Save step, so fall back to what it last reported.
    const liveDetails = this.previewMap?.getMapDetails() ?? m.mapDetails;
    if (liveDetails == undefined) {
      this.toastr.error('The map is not ready yet. Go back and wait for the map to load.');
      return;
    }
    const mapDetails = JSON.stringify({ ...liveDetails, timeZone: m.selectedTimeZone });
    if (mapDetails.length > 8000) {
      this.toastr.error('Too many open pin popups to save. Close some and try again.');
      return;
    }

    this.saving = true;

    const request: MapFavoriteRequest = {
      id: edit != null ? edit.id : -1,
      name: m.favoriteName.trim(),
      description: edit != null ? edit.description : null,
      sensorIDs: m.selectedSensors.map(s => s.id),
      sensorGroupIDs: m.selectedSensorGroups.map(g => g.id),
      sensorReadingTypeIDs: m.selectedReadingTypes.map(t => t.id),
      dateRange: encodeDateRange(m.dateRangeName, m.dateRange),
      mapDetails,
      userName: edit != null ? edit.userName : this.username,
      userGroupId: (m.favoriteUserGroup != null && m.favoriteUserGroup.id > -1) ? m.favoriteUserGroup.id : null
    };

    const save$: Observable<number> = edit != null
      ? this.mapFavoriteService.updateMapFavorite(request, this.username).pipe(map(() => edit.id))
      : this.mapFavoriteService.createMapFavorite(request).pipe(map((newId: any) => Number(newId)));

    save$.pipe(
      switchMap((id: number) => {
        if (isNaN(id) || id <= 0) {
          throw new Error('Invalid map favorite id');
        }
        return this.syncMapLabels(id, m).pipe(map(() => id));
      })
    ).subscribe(
      (id: number) => {
        if (edit != null) {
          this.saving = false;
          this.toastr.success('Map favorite updated successfully.');
          this.mapFavoriteUpdated.emit();
          return;
        }
        this.toastr.success('Map favorite created successfully.');
        this.attachMapFavoriteToDashboard(id, m);
      },
      () => {
        this.saving = false;
        this.toastr.error('Error occurred while saving the map favorite.');
      }
    );
  }

  private syncMapLabels(mapFavoriteId: number, m: MapFavoriteWizardModel): Observable<any> {
    const keptIds = new Set(m.labels.map(l => l.id));
    const calls: Observable<any>[] = [
      ...m.labels.filter(l => l.id <= 0)
                 .map(l => this.mapFavoriteService.createMapLabel({ ...l, id: 0, mapFavoriteID: mapFavoriteId })),
      ...m.labels.filter(l => l.id > 0)
                 .map(l => this.mapFavoriteService.updateMapLabel({ ...l, mapFavoriteID: mapFavoriteId }, this.username)),
      ...m.originalLabelIds.filter(id => !keptIds.has(id))
                           .map(id => this.mapFavoriteService.deleteMapLabel(id, this.username))
    ];
    return calls.length > 0 ? forkJoin(calls) : of([]);
  }

  // Attach the new map favorite to a new or existing dashboard (map tab enabled).
  private attachMapFavoriteToDashboard(mapFavoriteId: number, m: MapFavoriteWizardModel): void {
    if (m.dashboardTargetMode === 'existing') {
      const d = m.targetDashboard;
      const request: DashboardRequest = {
        id: d.id,
        name: d.name,
        eventIDs: d.eventIds || [],
        chartFavoriteIds: d.chartFavoriteIds || [],
        imageFavoriteIds: d.imageFavoriteIds || [],
        mapFavoriteIds: Array.from(new Set([...(d.mapFavoriteIds || []), mapFavoriteId])),
        userName: this.username,
        userGroup: d.userGroup,
        userGroupID: d.userGroupId,
        includeChartsTab: d.includeChartsTab,
        includeEventsTab: d.includeEventsTab,
        includeIsalTab: d.includeIsalTab,
        includeImageFavoritesTab: d.includeImageFavoritesTab,
        includeMapFavoritesTab: 1,                         // ensure the map favorite is visible
        computeActionSumCountsForEvents: d.computeActionSumCountsForEvents
      };
      this.dashboardEventsService.updateDashboard(request, this.username).subscribe(
        () => { this.saving = false; this.toastr.success('Dashboard updated successfully.'); this.created.emit(d.id); },
        () => { this.saving = false; this.toastr.error('Error occurred while updating the dashboard.'); }
      );
    } else {
      const groupId = (m.favoriteUserGroup != null && m.favoriteUserGroup.id > -1) ? m.favoriteUserGroup.id : null;
      const request: DashboardRequest = {
        id: undefined,
        name: m.newDashboardName.trim(),
        eventIDs: [],
        chartFavoriteIds: [],
        imageFavoriteIds: [],
        mapFavoriteIds: [mapFavoriteId],
        userName: this.username,
        userGroup: m.favoriteUserGroup != null ? m.favoriteUserGroup.name : '',
        userGroupID: groupId,
        includeChartsTab: 0,
        includeEventsTab: 0,
        includeIsalTab: 0,
        includeImageFavoritesTab: 0,
        includeMapFavoritesTab: 1,
        computeActionSumCountsForEvents: 0
      };
      this.dashboardEventsService.createDashboard(request).subscribe(
        result => { this.saving = false; this.toastr.success('Dashboard created successfully.'); this.created.emit(typeof result === 'number' ? result : null); },
        () => { this.saving = false; this.toastr.error('Error occurred while creating the dashboard.'); }
      );
    }
  }

  cancel(): void {
    const hasProgress = this.isEditMode || this.state.creationType != null || this.state.furthestStepReached > 1;
    if (hasProgress) {
      this.confirmationService.confirm({
        key: 'wizardCancel',
        message: this.isEditMode
          ? 'Discard your changes and close the editor?'
          : 'Discard this new creation and leave the wizard?',
        header: 'Cancel',
        icon: 'pi pi-exclamation-triangle',
        accept: () => this.cancelled.emit()
      });
    } else {
      this.cancelled.emit();
    }
  }
}
